import { getProducts, getProductById } from '@/mock/products';

export default {
  namespaced: true,
  state: {
    products: [],
    currentProduct: null,
    loading: false,
    error: null,
    filters: {
      category: null,
      minPrice: null,
      maxPrice: null,
      inStock: false,
      search: ''
    },
    sort: 'default',
    pagination: {
      currentPage: 1,
      pageSize: 12,
      total: 0
    }
  },
  getters: {
    allProducts: (state) => state.products,
    currentProduct: (state) => state.currentProduct,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    filteredProducts: (state) => {
      let filtered = [...state.products];
      
      if (state.filters.category) {
        filtered = filtered.filter(p => p.categoryId === state.filters.category);
      }
      
      if (state.filters.minPrice) {
        filtered = filtered.filter(p => p.price >= state.filters.minPrice);
      }
      
      if (state.filters.maxPrice) {
        filtered = filtered.filter(p => p.price <= state.filters.maxPrice);
      }
      
      if (state.filters.inStock) {
        filtered = filtered.filter(p => p.stock > 0);
      }
      
      if (state.filters.search) {
        const searchTerm = state.filters.search.toLowerCase();
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(searchTerm) || 
          p.description.toLowerCase().includes(searchTerm)
        );
      }
      
      return filtered;
    },
    sortedProducts: (state, getters) => {
      const products = [...getters.filteredProducts];
      
      switch (state.sort) {
        case 'price-asc':
          return products.sort((a, b) => a.price - b.price);
        case 'price-desc':
          return products.sort((a, b) => b.price - a.price);
        case 'name-asc':
          return products.sort((a, b) => a.name.localeCompare(b.name));
        case 'name-desc':
          return products.sort((a, b) => b.name.localeCompare(a.name));
        case 'rating':
          return products.sort((a, b) => b.rating - a.rating);
        default:
          return products;
      }
    },
    paginatedProducts: (state, getters) => {
      const start = (state.pagination.currentPage - 1) * state.pagination.pageSize;
      const end = start + state.pagination.pageSize;
      return getters.sortedProducts.slice(start, end);
    },
    totalPages: (state, getters) => {
      return Math.ceil(getters.filteredProducts.length / state.pagination.pageSize);
    }
  },
  mutations: {
    SET_PRODUCTS(state, products) {
      state.products = products;
      state.pagination.total = products.length;
    },
    SET_CURRENT_PRODUCT(state, product) {
      state.currentProduct = product;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_FILTER(state, { key, value }) {
      state.filters[key] = value;
      state.pagination.currentPage = 1;
    },
    SET_SORT(state, sort) {
      state.sort = sort;
    },
    SET_PAGE(state, page) {
      state.pagination.currentPage = page;
    },
    RESET_FILTERS(state) {
      state.filters = {
        category: null,
        minPrice: null,
        maxPrice: null,
        inStock: false,
        search: ''
      };
      state.sort = 'default';
      state.pagination.currentPage = 1;
    }
  },
  actions: {
    async fetchProducts({ commit }) {
      commit('SET_LOADING', true);
      try {
        const products = await getProducts();
        commit('SET_PRODUCTS', products);
      } catch (error) {
        commit('SET_ERROR', error.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async fetchProductById({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const product = await getProductById(id);
        commit('SET_CURRENT_PRODUCT', product);
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    updateFilter({ commit }, { key, value }) {
      commit('SET_FILTER', { key, value });
    },
    updateSort({ commit }, sort) {
      commit('SET_SORT', sort);
    },
    setPage({ commit }, page) {
      commit('SET_PAGE', page);
    },
    resetFilters({ commit }) {
      commit('RESET_FILTERS');
    }
  }
};